/* Instructor surface: create classes, share join codes, and open each class's
 * console (leaderboard / students / teams / grading / settings) by clicking it. */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Opponents } from '@venturewrist/shared';
import { useClasses, useCreateClass } from '../../hooks/queries';
import { Button, Card, Field, Input, Select, Spinner, EmptyNote, Badge } from '../../components/ui';
import { ApiError } from '../../lib/api';

export function InstructorDashboard() {
  const classes = useClasses();
  const create = useCreateClass();
  const [name, setName] = useState('');
  const [simLength, setSimLength] = useState(16);
  const [opponents, setOpponents] = useState<Opponents>('ai');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      { name, simLength, mode: { grouping: 'teams', opponents, teamSize: 3, aiCompanies: 3 } },
      { onSuccess: () => setName('') },
    );
  };

  return (
    <div className="grid gap-6 md:grid-cols-[1fr_320px]">
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-mut">Your classes</h2>
        {classes.isLoading ? (
          <Spinner />
        ) : (classes.data?.length ?? 0) === 0 ? (
          <EmptyNote>No classes yet — create one to get a join code.</EmptyNote>
        ) : (
          classes.data!.map((c) => (
            <Link key={c.id} to={`/classes/${c.id}`} className="block">
              <Card className="flex items-center justify-between transition hover:border-acc/40">
                <div>
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-mut">
                    {c.simLength} quarters · {c._count?.enrollments ?? 0} students · {c._count?.teams ?? 0} teams
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase text-mut">Join code</div>
                  <div className="font-mono text-lg font-bold tracking-widest text-acc">{c.joinCode}</div>
                </div>
              </Card>
            </Link>
          ))
        )}
      </section>

      <Card className="h-fit">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-mut">New class</h2>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Class name">
            <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="MGMT 480" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quarters">
              <Input type="number" min={1} max={40} value={simLength} onChange={(e) => setSimLength(Number(e.target.value))} />
            </Field>
            <Field label="Opponents">
              <Select value={opponents} onChange={(e) => setOpponents(e.target.value as Opponents)}>
                <option value="ai">AI rivals</option>
                <option value="pvp">Player vs player</option>
                <option value="mixed">Mixed</option>
              </Select>
            </Field>
          </div>
          {opponents !== 'ai' && <Badge tone="warn">PvP/mixed play arrives in Phase 5; games run vs AI for now.</Badge>}
          {create.error && <div className="text-sm text-bad">{(create.error as ApiError).message}</div>}
          <Button type="submit" className="w-full" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create class'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
